import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Popper, Fade } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroChart } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';
import { useTheme } from '@mui/material';

/**
 * Simple Success Rate Table - Progressive loading success rate daily data
 * 
 * Shows device list immediately, then loads success rate data progressively
 * from success-rate-daily endpoint, displaying comprehensive success analysis
 * with digit position failure tracking.
 */
const SimpleSuccessRateTable = ({ selectedOrgId, startDate, endDate }) => {
  const theme = useTheme();
  const [successRateData, setSuccessRateData] = useState({});
  
  // Hover overlay state
  const [hoverData, setHoverData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo
  } = useSharedPerformanceCache();

  useEffect(() => {
    loadSuccessRateData();
  }, [selectedOrgId, startDate, endDate]);

  const loadSuccessRateData = async () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    console.log('📊 SimpleSuccessRateTable: Checking cache...');
    
    // Trigger shared cache loading (will use cache if available)
    await loadAllPerformanceData(selectedOrgId, startDate, endDate);
    
    // Get data from shared cache
    const devices = getDevices();
    const successRateMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('success-rate-daily', device.id);
      if (data) {
        successRateMap[device.id] = data;
      }
    });
    
    setSuccessRateData(successRateMap);
    console.log(`📊 SimpleSuccessRateTable: Using shared cache data for ${Object.keys(successRateMap).length} devices`);
  };

  // Get signal quality color based on success rate
  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return '#4caf50'; // excellent - green
    if (rate >= 70) return '#ff9800'; // fair - orange  
    return '#f44336'; // poor - red
  };

  // Handle hover for detailed success rate breakdown
  const handleCellHover = (event, device, date) => {
    const deviceData = successRateData[device.id];
    const dayData = deviceData?.dailySuccessRate?.find(day => day.date === date);
    
    if (dayData && dayData.digitsAnalysis?.totalReadings > 0) {
      // Calculate position-specific success rates
      const positionSuccessRates = dayData.digitsAnalysis.positionFailures.map((failures, index) => {
        const total = dayData.digitsAnalysis.totalReadings;
        const successRate = total > 0 ? Math.round(((total - failures) / total) * 100) : 0;
        return { position: index + 1, successRate, failures };
      });
      
      setHoverData({
        device,
        date,
        dayData,
        positionSuccessRates
      });
      setAnchorEl(event.currentTarget);
      setOpen(true);
    }
  };

  const handleCellLeave = () => {
    setOpen(false);
    setHoverData(null);
    setAnchorEl(null);
  };
  
  // Calculate digit failure histograms with dynamic digit positions
  const calculateDigitFailureHistograms = () => {
    if (!successRateData || Object.keys(successRateData).length === 0) {
      return { readFailures: [], confidenceFailures: [], totalReadings: 0, maxDigitPositions: 0 };
    }
    
    // Find maximum digit positions across all devices
    let maxDigitPositions = 0;
    Object.values(successRateData).forEach(deviceData => {
      if (deviceData.summary?.maxDigitPositions) {
        maxDigitPositions = Math.max(maxDigitPositions, deviceData.summary.maxDigitPositions);
      }
    });
    
    // Aggregate position failures and confidence issues across all devices and dates
    const positionReadFailures = Array(maxDigitPositions || 10).fill(0);
    const positionConfidenceFailures = Array(maxDigitPositions || 10).fill(0);
    let totalReadings = 0;
    let statusInconsistencies = 0;
    
    Object.values(successRateData).forEach(deviceData => {
      deviceData.dailySuccessRate?.forEach(dayData => {
        if (dayData.digitsAnalysis?.totalReadings) {
          totalReadings += dayData.digitsAnalysis.totalReadings;
          
          // Check for status data inconsistencies
          const statusTotal = (dayData.statusAnalysis?.statusOk || 0) + (dayData.statusAnalysis?.statusFailedRead || 0);
          if (statusTotal > dayData.digitsAnalysis.totalReadings) {
            statusInconsistencies++;
            console.warn(`⚠️ Status inconsistency detected: ${statusTotal} status events > ${dayData.digitsAnalysis.totalReadings} total readings for ${dayData.date}`);
          }
          
          // Aggregate position failures (digit = -1) - only up to actual digit count
          dayData.digitsAnalysis.positionFailures?.forEach((failures, index) => {
            if (index < (maxDigitPositions || 10)) {
              positionReadFailures[index] += failures;
            }
          });
          
          // Aggregate confidence failures (score < 0.9) - only up to actual digit count
          dayData.scoresAnalysis?.positionLowConfidence?.forEach((lowConf, index) => {
            if (index < (maxDigitPositions || 10)) {
              positionConfidenceFailures[index] += lowConf;
            }
          });
        }
      });
    });
    
    // Convert to histogram data - only show relevant positions
    const actualPositions = maxDigitPositions || 10;
    const readFailures = positionReadFailures.slice(0, actualPositions).map((failures, index) => ({
      position: index + 1,
      count: failures,
      percentage: totalReadings > 0 ? Math.round((failures / totalReadings) * 1000) / 10 : 0
    }));
    
    const confidenceFailures = positionConfidenceFailures.slice(0, actualPositions).map((failures, index) => ({
      position: index + 1, 
      count: failures,
      percentage: totalReadings > 0 ? Math.round((failures / totalReadings) * 1000) / 10 : 0
    }));
    
    return { 
      readFailures, 
      confidenceFailures, 
      totalReadings, 
      maxDigitPositions: actualPositions,
      statusInconsistencies 
    };
  };
  
  const { readFailures, confidenceFailures, totalReadings, maxDigitPositions, statusInconsistencies } = calculateDigitFailureHistograms();
  
  // Create ECharts options for read failures histogram
  const createReadFailuresChart = () => {
    return {
      title: {
        text: 'Read Failures by Digit Position',
        textStyle: {
          fontSize: 14,
          color: theme.palette.waltero.primary.coreBlue,
          fontFamily: 'Inter'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const data = params[0];
          const position = data.dataIndex + 1;
          const failure = readFailures[data.dataIndex];
          return `Position ${position}<br/>Read Failures: ${failure.count}<br/>Rate: ${failure.percentage}%`;
        }
      },
      xAxis: {
        type: 'category',
        data: readFailures.map(f => `P${f.position}`),
        axisLabel: {
          fontSize: 11,
          color: theme.palette.text.secondary
        }
      },
      yAxis: {
        type: 'value',
        name: 'Failures',
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.secondary
        }
      },
      series: [{
        type: 'bar',
        data: readFailures.map(f => f.count),
        itemStyle: {
          color: '#f44336'
        },
        barWidth: '60%'
      }]
    };
  };
  
  // Create ECharts options for confidence failures histogram
  const createConfidenceFailuresChart = () => {
    return {
      title: {
        text: 'Confidence Failures by Digit Position',
        textStyle: {
          fontSize: 14,
          color: theme.palette.waltero.primary.coreBlue,
          fontFamily: 'Inter'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const data = params[0];
          const position = data.dataIndex + 1;
          const failure = confidenceFailures[data.dataIndex];
          return `Position ${position}<br/>Confidence Failures: ${failure.count}<br/>Rate: ${failure.percentage}%`;
        }
      },
      xAxis: {
        type: 'category',
        data: confidenceFailures.map(f => `P${f.position}`),
        axisLabel: {
          fontSize: 11,
          color: theme.palette.text.secondary
        }
      },
      yAxis: {
        type: 'value',
        name: 'Failures',
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.secondary
        }
      },
      series: [{
        type: 'bar',
        data: confidenceFailures.map(f => f.count),
        itemStyle: {
          color: '#ff9800'
        },
        barWidth: '60%'
      }]
    };
  };

  // Get devices from shared cache
  const devices = getDevices();

  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];

  if (cacheInfo.isLoading && devices.length === 0) {
    return (
      <WalteroCard sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading devices...</Typography>
      </WalteroCard>
    );
  }

  return (
    <WalteroCard sx={{ p: 3 }}>
      <WalteroTypography variant="h6" sx={{ mb: 2 }}>
        Device Success Rate - Daily Analysis (Optimized)
      </WalteroTypography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Daily success rate analysis with comprehensive digit position tracking. 
        Uses advanced TimeStream aggregation combining digits, scores, and status data.
        Data cached across tab switches - loads once per org/date range.
      </Typography>
      
      {cacheInfo.isLoading && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: '600' }}>
            📊 Loading Progress: {cacheInfo.endpointsLoaded} endpoints loaded across {cacheInfo.devicesLoaded}/{cacheInfo.totalDevices} devices
          </Typography>
        </Box>
      )}

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8' }}>
                    Device
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8' }}>
                    Meter
                  </TableCell>
                  {sortedDates.map(date => (
                    <TableCell 
                      key={date} 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: '#e8f5e8',
                        minWidth: '80px',
                        fontSize: '12px'
                      }}
                    >
                      {date.split('-').slice(1).join('/')}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8' }}>
                    Overall
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.sort((a, b) => {
                  const serialA = String(a.serialnumber || '');
                  const serialB = String(b.serialnumber || '');
                  return serialA.localeCompare(serialB);
                }).map(device => {
                  const deviceSuccessRate = successRateData[device.id];
                  const overallRate = deviceSuccessRate?.summary?.combinedSuccessRate || 0;
                  
                  return (
                    <TableRow key={device.id}>
                      <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        {device.serialnumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: '10px', color: '#666' }}>
                        {device.externalmeterid || 'N/A'}
                      </TableCell>
                      {sortedDates.map(date => {
                        const dayData = deviceSuccessRate?.dailySuccessRate?.find(day => day.date === date);
                        const successRate = dayData?.combinedSuccessRate;
                        
                        return (
                          <TableCell 
                            key={date} 
                            align="center"
                            sx={{
                              backgroundColor: successRate !== undefined ? '#fafafa' : '#f5f5f5',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: successRate !== undefined ? getSuccessRateColor(successRate) : '#999',
                              cursor: successRate !== undefined ? 'pointer' : 'default'
                            }}
                            onMouseEnter={(e) => handleCellHover(e, device, date)}
                            onMouseLeave={handleCellLeave}
                          >
                            {successRate !== undefined ? (
                              `${successRate}%`
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: '600', 
                          backgroundColor: '#e8f5e8',
                          color: overallRate > 0 ? getSuccessRateColor(overallRate) : '#999'
                        }}
                        title={deviceSuccessRate ? `
Overall Success Rate: ${overallRate}%
Worst Position: ${deviceSuccessRate.summary?.worstPosition?.position || 'N/A'} (${deviceSuccessRate.summary?.worstPosition?.successRate || 0}%)
Total Days: ${deviceSuccessRate.summary?.totalDays || 0}
                        `.trim() : 'No data available'}
                      >
                        {overallRate > 0 ? `${overallRate}%` : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Success Rate Detail Hover Overlay */}
          <Popper 
            open={open} 
            anchorEl={anchorEl} 
            placement="top" 
            transition
            style={{ zIndex: 1000 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={200}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    maxWidth: 350, 
                    backgroundColor: '#333', 
                    color: 'white',
                    fontSize: '12px'
                  }}
                >
                  {hoverData && (
                    <div>
                      <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1, color: 'white' }}>
                        {hoverData.device.serialnumber} - {hoverData.date}
                      </Typography>
                      
                      {/* Overall Success Rates */}
                      <Typography variant="body2" sx={{ mb: 1, color: '#4fc3f7', fontWeight: '600' }}>
                        Overall Success Rates:
                      </Typography>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Combined Rate:</span>
                        <span style={{ fontWeight: '600', color: getSuccessRateColor(hoverData.dayData.combinedSuccessRate) }}>
                          {hoverData.dayData.combinedSuccessRate}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Digit Recognition:</span>
                        <span style={{ fontWeight: '600', color: getSuccessRateColor(hoverData.dayData.digitSuccessRate) }}>
                          {hoverData.dayData.digitSuccessRate}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Status Validation:</span>
                        <span style={{ fontWeight: '600', color: getSuccessRateColor(hoverData.dayData.statusSuccessRate) }}>
                          {hoverData.dayData.statusSuccessRate}%
                        </span>
                      </div>

                      {/* Status Counts with Validation */}
                      <Typography variant="body2" sx={{ mb: 1, color: '#a5d6a7', fontWeight: '600' }}>
                        Status Breakdown:
                      </Typography>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#4caf50' }}>OK Reads:</span>
                        <span style={{ fontWeight: '600' }}>{hoverData.dayData.statusAnalysis.statusOk}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#f44336' }}>Failed Reads:</span>
                        <span style={{ fontWeight: '600' }}>{hoverData.dayData.statusAnalysis.statusFailedRead}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#ffcc80' }}>Total Status Events:</span>
                        <span style={{ fontWeight: '600' }}>{(hoverData.dayData.statusAnalysis.statusOk || 0) + (hoverData.dayData.statusAnalysis.statusFailedRead || 0)}</span>
                      </div>
                      {hoverData.dayData.statusAnalysis.isInconsistent && (
                        <div style={{ backgroundColor: '#f44336', padding: '4px', borderRadius: '4px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '600' }}>⚠️ Data Inconsistency: Status events exceed total readings!</span>
                        </div>
                      )}

                      {/* Position-Specific Success Rates */}
                      <Typography variant="body2" sx={{ mb: 1, color: '#ffcc80', fontWeight: '600' }}>
                        Position Success Rates (1-{maxDigitPositions}):
                      </Typography>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(maxDigitPositions, 5)}, 1fr)`, gap: '4px', marginBottom: '8px' }}>
                        {hoverData.positionSuccessRates.slice(0, maxDigitPositions).map((pos) => (
                          <div key={pos.position} style={{ 
                            textAlign: 'center',
                            padding: '2px',
                            backgroundColor: pos.failures > 0 ? '#ff5722' : '#4caf50',
                            borderRadius: '2px',
                            fontSize: '10px'
                          }}>
                            <div style={{ fontWeight: '600' }}>P{pos.position}</div>
                            <div>{pos.successRate}%</div>
                          </div>
                        ))}
                      </div>

                      {/* Reading Statistics */}
                      <hr style={{ margin: '8px 0', borderColor: '#555' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
                        <span>Total Readings:</span>
                        <span style={{ color: '#4fc3f7' }}>
                          {hoverData.dayData.digitsAnalysis.totalReadings}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Successful:</span>
                        <span style={{ color: '#4caf50' }}>
                          {hoverData.dayData.digitsAnalysis.successfulReadings}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Failed:</span>
                        <span style={{ color: '#f44336' }}>
                          {hoverData.dayData.digitsAnalysis.failedReadings}
                        </span>
                      </div>
                    </div>
                  )}
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(successRateData).length} with success rate data, {sortedDates.length} days
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Colors: Green (≥90%), Orange (70-89%), Red (&lt;70%) - Hover cells for detailed position analysis
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Total readings analyzed: {totalReadings.toLocaleString()} | Digit positions: {maxDigitPositions}
          {statusInconsistencies > 0 && (
            <span style={{ color: '#f44336', fontWeight: 'bold' }}>
              | ⚠️ {statusInconsistencies} days with status data inconsistencies
            </span>
          )}
        </Typography>
      </Box>
      
      {/* Digit Failure Analysis Histograms */}
      {totalReadings > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 3 }}>
          {/* Read Failures Histogram */}
          <WalteroCard sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              OCR read failures (digit = -1) by position. Shows positions 1-{maxDigitPositions} where digit recognition completely fails.
            </Typography>
            <WalteroChart
              option={createReadFailuresChart()}
              height={250}
              style={{ width: '100%' }}
              aria-label="Read failures by digit position histogram"
            />
          </WalteroCard>
          
          {/* Confidence Failures Histogram */}
          <WalteroCard sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Low confidence failures (score &lt; 0.9) by position. Shows positions 1-{maxDigitPositions} where OCR has uncertainty in readings.
            </Typography>
            <WalteroChart
              option={createConfidenceFailuresChart()}
              height={250}
              style={{ width: '100%' }}
              aria-label="Confidence failures by digit position histogram"
            />
          </WalteroCard>
        </Box>
      )}
    </WalteroCard>
  );
};

export default SimpleSuccessRateTable;